import React, { useEffect, useState } from 'react';
import { App, Button, Form, Image, Input, Select, Table } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getExternalProducts, OperatorType } from '../../api/external';

const { Option } = Select;

interface GoodsItem {
  edit: string;
  config: Config;
  micro_store_status: number;
  goods_id: number;
  template: string;
  copy: string;
  p_goods_name: string;
  p_goods_image: string;
  charge_description: string;
  is_ydchama: number;
  province_tag?: string;
}

interface Config {
  current: string;
  directional: string;
  telephone: string;
  original_monthly: string;
  current_monthly: string;
  charge_description: string;
  goods_subtitle: string;
}

interface HaoyiQueryParams {
  page: number;
  type: OperatorType;
  keyword?: string;
}

const HaoyiTab: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<GoodsItem[]>([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form] = Form.useForm();
  const [queryParams, setQueryParams] = useState<HaoyiQueryParams>({
    page: 1,
    type: OperatorType.ALL,
  });

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await getExternalProducts(queryParams);
      const data = response as unknown as {
        total: number;
        per_page: number;
        current_page: number;
        last_page: number;
        data: GoodsItem[];
      };
      setProducts(data.data);
      setTotal(data.total);
      setCurrent(data.current_page);
      setPageSize(data.per_page);
    } catch (error: any) {
      messageApi.error(error.response?.data?.message || '获取外部产品列表失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [queryParams]);

  const columns: ColumnsType<GoodsItem> = [
    {
      title: '商品ID',
      dataIndex: 'goods_id',
      width: 80,
    },
    {
      title: '商品名称',
      dataIndex: 'p_goods_name',
      width: 200,
      ellipsis: true,
    },
    {
      title: '商品图片',
      dataIndex: 'p_goods_image',
      width: 80,
      render: (image) => (
        <Image src={image} alt="" width={50} height={50} style={{ objectFit: 'cover' }} />
      ),
    },
    {
      title: '当前流量',
      dataIndex: ['config', 'current'],
      width: 100,
      ellipsis: true,
    },
    {
      title: '定向流量',
      dataIndex: ['config', 'directional'],
      width: 100,
      ellipsis: true,
    },
    {
      title: '通话时长',
      dataIndex: ['config', 'telephone'],
      width: 100,
      ellipsis: true,
    },
    {
      title: '原月租',
      dataIndex: ['config', 'original_monthly'],
      width: 80,
    },
    {
      title: '当前月租',
      dataIndex: ['config', 'current_monthly'],
      width: 80,
    },
    {
      title: '省份标签',
      dataIndex: 'province_tag',
      width: 80,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <>
          <a href={record.edit} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>编辑</a>
          <a href={record.template} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>详情</a>
          <a href={record.copy} target="_blank" rel="noopener noreferrer">复制</a>
        </>
      ),
    },
  ];

  const handleSearch = (values: any) => {
    setQueryParams({ ...queryParams, ...values, page: 1 });
  };

  const handleReset = () => {
    form.resetFields();
    setQueryParams({ page: 1, type: OperatorType.ALL });
  };

  return (
    <div style={{ padding: 0 }}>
      <div style={{ marginBottom: 12 }}>
        <Form form={form} layout="inline" onFinish={handleSearch} initialValues={queryParams}>
          <Form.Item name="keyword" style={{ marginBottom: 8 }}>
            <Input placeholder="搜索商品名称" prefix={<SearchOutlined />} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="type" style={{ marginBottom: 8 }}>
            <Select placeholder="选择运营商" style={{ width: 120 }}>
              <Option value={OperatorType.ALL}>全部</Option>
              <Option value={OperatorType.MOBILE}>移动</Option>
              <Option value={OperatorType.UNICOM}>联通</Option>
              <Option value={OperatorType.TELECOM}>电信</Option>
              <Option value={OperatorType.BROADCAST}>广电</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button type="primary" htmlType="submit">搜索</Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
          </Form.Item>
        </Form>
      </div>

      <Table
        columns={columns}
        dataSource={products}
        rowKey="goods_id"
        loading={loading}
        scroll={{ x: 'max-content', y: 'calc(100vh - 310px)' }}
        pagination={{
          current,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, size) => {
            setCurrent(page);
            setPageSize(size || 10);
            setQueryParams({ ...queryParams, page });
          },
        }}
        size="middle"
      />
    </div>
  );
};


export default HaoyiTab;
